/**
 * usePoints — SilkReels 积分余额全局订阅
 *
 * 单例缓存 + inflight 去重，避免 Account 布局多组件重复请求 /points/balance。
 */
import { useCallback, useEffect, useState } from 'react'

import { ApiError, pointsApi, type SilkPointBalance } from '@/api'
import { subscribeAuth } from '@/api/authSession'
import { runAfterFirstPaint } from '@/shared/hooks/useLocaleRefetch'

interface State {
  /** AI 等消费可用总额（可打赏 + 促销 + 会员） */
  balance: number
  /** 可打赏 / 可提现桶（points_balance） */
  generalBalance: number
  promoBalance: number
  tippableAvailable: number
  subscriptionBonus: number
  frozen: number
  loading: boolean
  error: string | null
}

const INITIAL: State = {
  balance: 0,
  generalBalance: 0,
  promoBalance: 0,
  tippableAvailable: 0,
  subscriptionBonus: 0,
  frozen: 0,
  loading: false,
  error: null,
}

interface UsePointsOptions {
  poll?: boolean
  intervalMs?: number
  enabled?: boolean
}

let cache: State = { ...INITIAL }
let inflight: Promise<void> | null = null
let loadedForAuth = false
const listeners = new Set<() => void>()
let subscriberCount = 0
let pollTimer: ReturnType<typeof setInterval> | null = null
let pollSubscriberCount = 0

function notify() {
  for (const l of listeners) l()
}

function applyBalance(b: SilkPointBalance) {
  const bonus = b.subscription_bonus ?? 0
  const promo = b.promo_balance ?? 0
  const tippable = b.tippable_available ?? b.balance
  const total = b.total_available ?? b.balance + promo + bonus
  cache = {
    balance: total,
    generalBalance: b.balance,
    promoBalance: promo,
    tippableAvailable: tippable,
    subscriptionBonus: bonus,
    frozen: b.frozen,
    loading: false,
    error: null,
  }
  loadedForAuth = true
  notify()
}

export function resetPointsCache() {
  cache = { ...INITIAL }
  inflight = null
  loadedForAuth = false
  notify()
}

async function fetchOnce(force = false) {
  if (inflight) {
    await inflight
    return
  }
  if (!force && loadedForAuth) {
    return
  }
  cache = { ...cache, loading: !loadedForAuth, error: null }
  notify()
  inflight = pointsApi
    .balance()
    .then((b) => {
      applyBalance(b)
    })
    .catch((err) => {
      const msg = err instanceof ApiError ? err.message : (err as Error).message
      cache = { ...cache, loading: false, error: msg }
      notify()
    })
    .finally(() => {
      inflight = null
    })
  await inflight
}

function startPolling(intervalMs: number) {
  if (pollTimer) return
  pollTimer = setInterval(() => {
    void fetchOnce(true)
  }, intervalMs)
}

function stopPolling() {
  if (!pollTimer) return
  clearInterval(pollTimer)
  pollTimer = null
}

export function usePoints(opts: UsePointsOptions = {}) {
  const { poll = false, intervalMs = 5000, enabled = true } = opts
  const [state, setState] = useState(cache)

  const refresh = useCallback(async () => {
    if (!enabled) return
    await fetchOnce(true)
  }, [enabled])

  const checkin = useCallback(async () => {
    if (!enabled) return null
    const res = await pointsApi.checkin()
    const promo = res.promo_balance ?? cache.promoBalance
    const bonus = res.subscription_bonus ?? cache.subscriptionBonus
    cache = {
      ...cache,
      generalBalance: res.balance,
      promoBalance: promo,
      tippableAvailable: res.balance,
      balance: res.balance + promo + bonus,
      subscriptionBonus: bonus,
      frozen: res.frozen,
    }
    loadedForAuth = true
    notify()
    return res
  }, [enabled])

  useEffect(() => {
    subscriberCount += 1
    const sync = () => setState({ ...cache })
    listeners.add(sync)
    sync()

    if (enabled) {
      runAfterFirstPaint(() => void fetchOnce(false))
    }

    if (poll && enabled) {
      pollSubscriberCount += 1
      startPolling(intervalMs)
    }

    return () => {
      listeners.delete(sync)
      subscriberCount -= 1
      if (poll && enabled) {
        pollSubscriberCount -= 1
        if (pollSubscriberCount <= 0) {
          stopPolling()
        }
      }
    }
  }, [enabled, poll, intervalMs])

  if (!enabled) {
    return { ...INITIAL, loading: false, refresh, checkin }
  }

  return { ...state, refresh, checkin }
}

subscribeAuth(() => {
  resetPointsCache()
})
